import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Layout from "../../Component/Layout";
import axios from "axios";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";

// ── Nested row: fetches & renders per-material line items when Material Cost is expanded ──
const MaterialLineItems = ({ productionOrderNumber, mode }) => {
    const [items, setItems] = useState(null); // null = loading
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setItems(null);
        setError(false);
        axios
            .get(`${PURCHASE_API}/product-costing/material-breakdown`, {
                params: { productionOrderNumber, mode },
            })
            .then((res) => {
                if (!cancelled) setItems(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            });
        return () => { cancelled = true; };
    }, [productionOrderNumber, mode]);

    if (error) {
        return (
            <div style={{ padding: "10px 14px 10px 30px", fontSize: 11.5, color: "#b91c1c", background: "#fef2f2" }}>
                Couldn't load material details.
            </div>
        );
    }

    if (items === null) {
        return (
            <div style={{ padding: "10px 14px 10px 30px", fontSize: 11.5, color: "#94a3b8", background: "#f8fafc" }}>
                Loading materials…
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div style={{ padding: "10px 14px 10px 30px", fontSize: 11.5, color: "#94a3b8", background: "#f8fafc", fontStyle: "italic" }}>
                No material lines found.
            </div>
        );
    }

    // Actual mode also shows which batch of each material was consumed.
    const showBatch = mode === "actual";
    const gridCols = showBatch ? "1fr 120px 52px 56px 64px" : "1fr 52px 56px 64px";

    return (
        <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{
                display: "grid", gridTemplateColumns: gridCols,
                gap: 4, padding: "6px 14px 6px 30px", fontSize: 10, fontWeight: 700,
                color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.3
            }}>
                <span>Material</span>
                {showBatch && <span>Batch No</span>}
                <span style={{ textAlign: "right" }}>Qty</span>
                <span style={{ textAlign: "right" }}>Rate</span>
                <span style={{ textAlign: "right" }}>Cost</span>
            </div>
            {items.map((m, i) => (
                <div key={i} style={{
                    display: "grid", gridTemplateColumns: gridCols,
                    gap: 4, padding: "5px 14px 5px 30px", fontSize: 11.5,
                    borderTop: "1px solid #eef2f7", color: "#334155",
                    alignItems: "center"
                }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${m.productCode} — ${m.description || ""}`}>
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>{m.productCode}</span>
                        {m.description ? <span style={{ color: "#94a3b8" }}> · {m.description}</span> : null}
                    </span>
                    {showBatch && (
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.batchNo || ""}>
                            {m.batchNo || "-"}
                        </span>
                    )}
                    <span style={{ textAlign: "right" }}>{Number(m.qty || 0).toFixed(2)}</span>
                    <span style={{ textAlign: "right" }}>{Number(m.rate || 0).toFixed(2)}</span>
                    <span style={{ textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{Number(m.cost || 0).toFixed(2)}</span>
                </div>
            ))}
        </div>
    );
};

// Builds a Product Code dropdown option that always carries the description
const toProductCodeOption = (p) => {
    const code = (p && (p.productCode || p.product_code)) || (typeof p === "string" ? p : "");
    const desc = (p && (p.productDescription || p.description || p.product_description)) || "";
    return { value: code, label: desc ? `${code} — ${desc}` : code, code, description: desc };
};

// ── Document types available in the Document Type filter ──
const DOCUMENT_TYPES = ["Purchase Order", "Production Order", "Physical Inventory"];

// Classifies a costing row into one of DOCUMENT_TYPES
const getRowDocType = (o) => {
    if (o.documentType === "Physical Inventory") return "Physical Inventory";
    if (o.documentType === "Purchase Order") return "Purchase Order";
    if (o.sourceType === "goods_receipt") return "Goods Receipt";
    return "Production Order";
};

// Classifies an entry of the Document dropdown list. Uses an explicit type field from the
// API when present, otherwise falls back to the document-number prefix (PO..., PRD...).
const getDocNumberType = (p) => {
    const explicit = p && (p.document_type || p.documentType || p.doc_type);
    if (explicit) return explicit;
    const num = String((p && (p.document_number || p.production_order_number || p.purchase_order_number)) || p || "").toUpperCase();
    if (num.startsWith("PRD")) return "Production Order";
    if (num.startsWith("PINV")) return "Physical Inventory";
    if (num.startsWith("GR")) return "Goods Receipt";
    if (num.startsWith("PO")) return "Purchase Order";
    return "";
};

// ── Small popover showing the cost breakup (Material / Setup / Labour / Machine / Overhead) ──
// Rendered via a portal into document.body so it is never clipped or hidden by
// a scrollable/overflow:hidden table container, and always sits above other rows.
// The Material Cost row can be expanded further to show individual material line items.
const CostBreakdownPopover = ({ label, color, data, productionOrderNumber, mode }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const btnRef = React.useRef(null);
    const popoverRef = React.useRef(null);
    const POPOVER_WIDTH = 300;

    const computePosition = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        let left = rect.right - POPOVER_WIDTH + window.scrollX;
        // keep popover within the viewport horizontally
        if (left < 8) left = 8;
        if (left + POPOVER_WIDTH > window.innerWidth - 8) {
            left = window.innerWidth - POPOVER_WIDTH - 8;
        }
        setCoords({
            top: rect.bottom + window.scrollY + 6,
            left,
        });
    };

    useEffect(() => {
        if (!open) return;
        computePosition();

        const handleClickOutside = (e) => {
            if (
                btnRef.current && !btnRef.current.contains(e.target) &&
                popoverRef.current && !popoverRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const handleReposition = () => computePosition();

        document.addEventListener("mousedown", handleClickOutside);
        // capture=true so this fires for scroll on the inner table container too
        window.addEventListener("scroll", handleReposition, true);
        window.addEventListener("resize", handleReposition);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleReposition, true);
            window.removeEventListener("resize", handleReposition);
        };
    }, [open]);

    const otherRows = [
        { key: "setup", label: "Setup Cost", value: data.setup },
        { key: "labour", label: "Labour Cost", value: data.labour },
        { key: "machine", label: "Machine / Run Time Cost", value: data.machine },
        { key: "overhead", label: "Overhead Cost", value: data.overhead },
    ];
    const materialValue = parseFloat(data.material) || 0;
    const total = materialValue + otherRows.reduce((sum, r) => sum + (parseFloat(r.value) || 0), 0);

    const rowStyle = {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 14px", fontSize: 12.5,
        borderTop: "1px solid #f1f5f9", color: "#475569",
    };

    return (
        <span style={{ position: "relative", display: "inline-block", marginLeft: 4, flexShrink: 0 }}>
            <button
                ref={btnRef}
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                style={{
                    width: 15, height: 15, borderRadius: "50%", border: "1px solid #cbd5e1",
                    background: open ? color : "#f8fafc", color: open ? "#fff" : "#64748b",
                    fontSize: 9, fontWeight: 700, lineHeight: "13px", cursor: "pointer", padding: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "background 0.15s, color 0.15s"
                }}
                title={`${label} breakdown`}
            >
                i
            </button>

            {open && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: "absolute", top: coords.top, left: coords.left, zIndex: 9999,
                        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)", width: POPOVER_WIDTH,
                        overflow: "hidden", fontFamily: "inherit"
                    }}
                >
                    {/* Header */}
                    <div style={{
                        background: color, color: "#fff", fontSize: 13, fontWeight: 700,
                        padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}>
                        <span>{label} Breakdown</span>
                        <button
                            onClick={() => setOpen(false)}
                            style={{
                                background: "transparent", border: "none", color: "rgba(255,255,255,0.8)",
                                cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 2
                            }}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    <div>
                        {/* Remaining static rows */}
                        {otherRows.map((r) => (
                            <div key={r.key} style={rowStyle}>
                                <span>{r.label}</span>
                                <span style={{ fontWeight: 600, color: "#1e293b" }}>{(parseFloat(r.value) || 0).toFixed(2)}</span>
                            </div>
                        ))}

                        {/* Total */}
                        <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "10px 14px", fontSize: 13, fontWeight: 700,
                            background: "#f8fafc", color: "#0f172a", borderTop: "1px solid #e2e8f0"
                        }}>
                            <span>Total (incl. Material)</span>
                            <span>{total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </span>
    );
};


// ── dd-mm-yyyy helpers + calendar date field ──
const formatDMY = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
};

// Shows the date as dd-mm-yyyy with a calendar icon; clicking anywhere on it
// opens the browser's calendar picker (a transparent native date input sits on top).
const DateField = ({ value, onChange, placeholder, min, max }) => (
    <div style={{ position: "relative", height: 32, border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 8px", fontSize: 12, color: value ? "#1e293b" : "#94a3b8" }}>
        <span>{value ? formatDMY(value + "T00:00:00") : placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <input
            type="date"
            value={value}
            min={min}
            max={max}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch (err) { /* ignore */ } }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
        />
        {value && (
            <span
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                style={{ position: "absolute", right: 28, top: 0, height: 32, display: "flex", alignItems: "center", cursor: "pointer", color: "#94a3b8", zIndex: 2, padding: "0 4px" }}
                title="Clear"
            >×</span>
        )}
    </div>
);

const ProductCosting = () => {
    const location = useLocation();
    
    // ── Filter master data ──
    const [poFilterMasterData, setPoFilterMasterData] = useState({
        productTypes: [],
        costingLevels: [],
        constructions: [],
        codes: [],
        cableTypes: [],
        categories: [],
        cores: [],
        colours: [],
        nominalCrossSections: [],
        referenceOdStandards: [],
        voltages: [],
    });

    const emptyListingFilters = {
        construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
        subCategory: null, qualityCategory: null,
    };
    const [listingFilters, setListingFilters] = useState(emptyListingFilters);

    // Cascading filter options - same endpoint the Product Master screen uses,
    // so the dropdown values narrow down exactly like they do there.
    const [filterOptions, setFilterOptions] = useState({});
    const toStrOptions = (arr) => (arr || []).map(v => ({ value: v, label: v }));
    const [selectedType, setSelectedType] = useState("");
    const [selectedCostingLevel, setSelectedCostingLevel] = useState("");
    const [expandedMaterialRows, setExpandedMaterialRows] = useState({});
    const [expandedPlannedMaterialRows, setExpandedPlannedMaterialRows] = useState({});

    const [selectedProductionNo, setSelectedProductionNo] = useState("");
    const [selectedDocType, setSelectedDocType] = useState(""); // "" = all document types
    // "detailed" = existing row-per-document view, "summary" = one line per product code
    const [viewMode, setViewMode] = useState("detailed");
    const isSummary = viewMode === "summary";
    const [dateFrom, setDateFrom] = useState(""); // yyyy-mm-dd
    const [dateTo, setDateTo] = useState("");
    const [productionOrderNumbers, setProductionOrderNumbers] = useState([]);

    const [listingProductCode, setListingProductCode] = useState(null);
    const [productCodeMasterOptions, setProductCodeMasterOptions] = useState([]);

    const [selectedWorkcenter, setSelectedWorkcenter] = useState("");
    const [workcenterNames, setWorkcenterNames] = useState([]);


    const [orders, setOrders] = useState([]);

    // ── Pagination State ──
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // ── Fetch filter master data ──
    const fetchPoFilterMasterData = async () => {
        try {
            const [
                resProdTypes, resCostingLevels, resCons, resCode, resCable, resCat,
                resCore, resCol, resCross, resRef, resVolt
            ] = await Promise.all([
                fetch(`${MASTER_API}/configuration/product-types`),
                fetch(`${MASTER_API}/configuration/dynamic/costing-level`),
                fetch(`${MASTER_API}/configuration/construction`),
                fetch(`${MASTER_API}/configuration/dynamic/code`),
                fetch(`${MASTER_API}/configuration/cable-type`),
                fetch(`${MASTER_API}/configuration/category`),
                fetch(`${MASTER_API}/configuration/core`),
                fetch(`${MASTER_API}/configuration/colour`),
                fetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                fetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`),
                fetch(`${MASTER_API}/configuration/dynamic/voltage`),
            ]);
            const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];
            const costingLevelsData = resCostingLevels.ok ? await resCostingLevels.json() : [];
            setPoFilterMasterData({
                productTypes: prodTypesData.map(item => ({ value: item.name, label: item.name })).filter(o => o.value),
                costingLevels: costingLevelsData.map(item => ({ value: item.name, label: item.name })).filter(o => o.value),
                constructions: resCons.ok ? await resCons.json() : [],
                codes: resCode.ok ? await resCode.json() : [],
                cableTypes: resCable.ok ? await resCable.json() : [],
                categories: resCat.ok ? await resCat.json() : [],
                cores: resCore.ok ? await resCore.json() : [],
                colours: resCol.ok ? await resCol.json() : [],
                nominalCrossSections: resCross.ok ? await resCross.json() : [],
                referenceOdStandards: resRef.ok ? await resRef.json() : [],
                voltages: resVolt.ok ? await resVolt.json() : [],
            });
        } catch (err) {
            console.error("Failed fetching filter master data", err);
        }
    };

    const getPoFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category
                || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    // Changing a filter clears the filters that depend on it (same order as Product Master)
    const FILTER_DEPENDENTS = {
        category: ["cableType", "code", "color", "noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction", "subCategory", "qualityCategory"],
        cableType: ["code", "color", "noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction"],
        code: ["color", "noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction"],
        color: ["noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction"],
        noOfCores: ["nominalCrossSection", "referenceOfStandard", "voltage", "construction"],
        nominalCrossSection: ["referenceOfStandard", "voltage", "construction"],
        referenceOfStandard: ["voltage", "construction"],
        voltage: ["construction"],
        subCategory: ["qualityCategory"],
    };

    const handleListingFilter = (key, selectedOpt) => {
        setListingFilters(prev => {
            const next = { ...prev, [key]: selectedOpt };
            (FILTER_DEPENDENTS[key] || []).forEach(k => { next[k] = null; });
            return next;
        });
    };

    // Product Type drives which filters are shown (same as Product Master):
    //   Raw Material          -> Category, Sub Category, Quality Category
    //   Finished / Semi / none -> Category, Cable Type, Code, Color, Cores, Cross Section, Std. Ref, Voltage, Construction
    const selectedTypeLc = (selectedType || "").trim().toLowerCase();
    const isRawMaterial = selectedTypeLc === "raw material";

    const handleProductTypeChange = (opt) => {
        setSelectedType(opt ? opt.value : "");
        // type-specific filters are not valid across types, so reset them
        setListingFilters(emptyListingFilters);
        setListingProductCode(null);
    };

    const fetchFilterOptions = async () => {
        try {
            const v = (k) => (listingFilters[k] ? listingFilters[k].value : "");
            const res = await axios.get(`${MASTER_API}/product-master/filter-options`, {
                params: {
                    productType: selectedType || "",
                    category: v("category"),
                    cableType: v("cableType"),
                    code: v("code"),
                    color: v("color"),
                    noOfCores: v("noOfCores"),
                    nominalCrossSection: v("nominalCrossSection"),
                    referenceOfStandard: v("referenceOfStandard"),
                    voltage: v("voltage"),
                    subCategory: v("subCategory"),
                },
            });
            setFilterOptions(res.data || {});
        } catch (e) {
            console.error("Failed to fetch filter options", e);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType, listingFilters]);

    // Product Code list follows Product Type (same as Product Master).
    // With no type selected, keep the original costing product-code list.
    useEffect(() => {
        if (!selectedType) {
            axios.get(`${PURCHASE_API}/product-costing/product-codes`)
                .then((res) => setProductCodeMasterOptions(
                    (res.data || []).map(toProductCodeOption).filter(o => o.value)
                ))
                .catch(e => console.error("Failed fetching product codes", e));
            return;
        }
        axios.get(`${MASTER_API}/product-master/productCodeDescriptionList`, {
            params: { productType: selectedType },
        })
            .then((res) => setProductCodeMasterOptions(
                (res.data || []).map(toProductCodeOption).filter(o => o.value)
            ))
            .catch(e => console.error("Failed fetching product codes for type", e));
    }, [selectedType]);

    // ── Load Dropdowns ──
    useEffect(() => {
        fetchPoFilterMasterData();

        axios.get(`${PURCHASE_API}/product-costing/workcenters`)
            .then((res) => setWorkcenterNames(
                (res.data || []).map(w => w.workcenter_name || w.name || w).filter(Boolean)
            ))
            .catch(e => console.error("Failed fetching workcenters", e));

        axios.get(`${PURCHASE_API}/product-costing/order-numbers`)
            .then((res) => setProductionOrderNumbers(res.data || []))
            .catch(e => console.error("Failed fetching production order numbers", e));

    }, []);

    // ── Costing Run ──
    // Popup: Costing run type (Base metal based / Period based); Period based
    // shows a second dropdown (Monthly / Weekly / Previous day). The backend
    // updates product master prices from the average ACTUAL unit cost.
    const [costingRunning, setCostingRunning] = useState(false);
    const [showRunModal, setShowRunModal] = useState(false);
    const [runType, setRunType] = useState("");
    const [runPeriod, setRunPeriod] = useState("");
    const [runMessage, setRunMessage] = useState("");
    const [runStatus, setRunStatus] = useState("info"); // info | success | error

    const openRunModal = () => {
        setRunType("");
        setRunPeriod("");
        setRunMessage("");
        setRunStatus("info");
        setShowRunModal(true);
    };

    const handleCostingRun = async () => {
        if (!runType) { setRunStatus("error"); setRunMessage("Please select a costing run type."); return; }
        if (runType === "period" && !runPeriod) { setRunStatus("error"); setRunMessage("Please select a period."); return; }
        setCostingRunning(true);
        setRunMessage("");
        try {
            const res = await axios.post(`${PURCHASE_API}/product-costing/run-costing`, {
                runType,
                period: runType === "period" ? runPeriod : undefined,
            });
            setRunStatus("success");
            setRunMessage(res.data?.message || "Costing run completed.");
            await Promise.resolve(loadCostingOrders());
        } catch (err) {
            setRunStatus("error");
            setRunMessage(err?.response?.data?.error || "Costing run failed.");
        } finally {
            setCostingRunning(false);
        }
    };

    // ── Load List Data ──
    // Combines two independent sources into one table:
    //   1) production-order costing rows (batch_cost_snapshot driven)
    //   2) goods-receipt costing rows (fetched live from goods_receipt, nothing stored)
    // Both endpoints already return rows shaped with the same field names
    // (header_product_code, batch_no, general_qty, actual_cost, description, ...),
    // so they can sit in the same "orders" array/table without a separate tab.
    const loadCostingOrders = () => {
        const poRequest = axios.get(`${PURCHASE_API}/product-costing/product-list`, {
            params: {
                productType: selectedType || undefined,
                costingLevel: selectedCostingLevel || undefined,
                productCode: listingProductCode ? listingProductCode.value : undefined,
                workcenter: (!isSummary && selectedWorkcenter) || undefined,
                productionNo: selectedProductionNo || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                construction: listingFilters.construction ? listingFilters.construction.value : undefined,
                code: listingFilters.code ? listingFilters.code.value : undefined,
                cableType: listingFilters.cableType ? listingFilters.cableType.value : undefined,
                category: listingFilters.category ? listingFilters.category.value : undefined,
                noOfCores: listingFilters.noOfCores ? listingFilters.noOfCores.value : undefined,
                color: listingFilters.color ? listingFilters.color.value : undefined,
                nominalCrossSection: listingFilters.nominalCrossSection ? listingFilters.nominalCrossSection.value : undefined,
                referenceOfStandard: listingFilters.referenceOfStandard ? listingFilters.referenceOfStandard.value : undefined,
                voltage: listingFilters.voltage ? listingFilters.voltage.value : undefined,
                subCategory: listingFilters.subCategory ? listingFilters.subCategory.value : undefined,
                qualityCategory: listingFilters.qualityCategory ? listingFilters.qualityCategory.value : undefined,
            },
        }).then((res) => (res.data || []).map((row) => ({ ...row, sourceType: "production" })))
          .catch((err) => {
              console.error("Failed load production order list", err);
              return [];
          });

        // Goods Receipt list only understands productType / productCode as
        // overlapping filters with the PO list -- the rest (workcenter,
        // production order, cable-spec filters) don't apply to goods_receipt
        // at all, so we intentionally don't pass them through.
        const grRequest = axios.get(`${PURCHASE_API}/product-costing/goods-receipt-list`, {
            params: {
                productType: selectedType || undefined,
                productCode: listingProductCode ? listingProductCode.value : undefined,
                purchaseOrderId: selectedProductionNo || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            },
        }).then((res) => (res.data || []).map((row) => ({ ...row, sourceType: "goods_receipt" })))
          .catch((err) => {
              console.error("Failed load goods receipt cost list", err);
              return [];
          });

        // Goods Receipt rows have no production order / workcenter / cable spec,
        // so once any of those filters is applied they must not be shown.
        const grApplies = (isSummary || !selectedWorkcenter) && !selectedCostingLevel &&
            !Object.values(listingFilters).some(Boolean) &&
            (!selectedDocType || selectedDocType === "Physical Inventory");
        const grPromise = grApplies ? grRequest : Promise.resolve([]);

        Promise.all([poRequest, grPromise])
            .then(([poRows, grRows]) => setOrders(
                [...poRows, ...grRows].filter((r) => !selectedDocType || getRowDocType(r) === selectedDocType)
            ))
            .catch((err) => {
                console.error("Failed load costing list", err);
                setOrders([]);
            });
    };

    useEffect(() => {
        loadCostingOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType, selectedCostingLevel, listingProductCode, selectedWorkcenter, selectedProductionNo, selectedDocType, listingFilters, dateFrom, dateTo, viewMode]);

    // ── Summary view: one line per product code (same idea as Batch List's Summary tab).
    // Planned cost / order qty repeat on every batch row of the same document, so they are
    // counted once per document; actual cost is summed across every row that has one. ──
    const summaryRows = (() => {
        const num = (v) => (v === null || v === undefined || v === "" ? null : parseFloat(v));
        const map = new Map();
        const seenDocs = new Set();
        orders.forEach((o) => {
            const code = o.header_product_code || o.product_code;
            if (!code) return;
            if (!map.has(code)) {
                map.set(code, {
                    product_code: code,
                    productType: o.productType || o.product_type || "",
                    description: o.productDescription || o.description || "",
                    uom: o.uom || "",
                    currency: o.currency || "",
                    qty: 0,
                    hasPlanned: false, plannedMaterial: 0, plannedTotal: 0, plannedQty: 0,
                    hasActual: false, actualTotal: 0, actualQty: 0,
                });
            }
            const r = map.get(code);
            if (!r.uom && o.uom) r.uom = o.uom;
            if (!r.currency && o.currency) r.currency = o.currency;
            const qty = num(o.general_qty) || 0;

            const docKey = `${o.sourceType || o.documentType}|${o.document || o.production_order_number || o.purchase_order_number}|${code}`;
            if (!seenDocs.has(docKey)) {
                seenDocs.add(docKey);
                r.qty += qty;
                const pt = num(o.planned_cost);
                if (pt !== null) {
                    r.hasPlanned = true;
                    r.plannedTotal += pt;
                    r.plannedMaterial += num(o.planned_material_cost) || 0;
                    r.plannedQty += qty;
                }
            }
            const at = num(o.actual_cost);
            if (at !== null) {
                r.hasActual = true;
                r.actualTotal += at;
                r.actualQty += qty;
            }
        });
        const f = (v) => v.toFixed(2);
        return Array.from(map.values()).map((r) => ({
            ...r,
            planned_unit_cost: r.hasPlanned && r.plannedQty > 0 ? f(r.plannedTotal / r.plannedQty) : null,
            planned_material_cost: r.hasPlanned ? f(r.plannedMaterial) : null,
            planned_cost: r.hasPlanned ? f(r.plannedTotal) : null,
            unit_cost: r.hasActual && r.actualQty > 0 ? f(r.actualTotal / r.actualQty) : null,
            actual_material_cost: r.hasActual ? f(r.actualTotal) : null,
            actual_cost: r.hasActual ? f(r.actualTotal) : null,
        }));
    })();

    const filteredOrders = isSummary ? summaryRows : orders;

    // Document dropdown only lists numbers of the selected Document Type (all when none selected)
    const documentOptions = productionOrderNumbers
        .filter((p) => !selectedDocType || getDocNumberType(p) === selectedDocType)
        .map((p) => {
            const v = p.document_number || p.production_order_number || p.purchase_order_number || p;
            return { value: v, label: v };
        });

    // ── Pagination Logic ──
    // Reset to page 1 whenever any filter changes the total dataset size
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedType, listingProductCode, selectedWorkcenter, selectedProductionNo, selectedDocType, listingFilters, orders, viewMode]);

    const indexOfLastOrder = currentPage * itemsPerPage;
    const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
    const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    // ── Shared react-select styling ──
    const compactSelectStyles = {
        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
        indicatorsContainer: (base) => ({ ...base, height: 32 }),
        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
        indicatorSeparator: () => ({ display: "none" }),
        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
    };

    return (
        <Layout defaultSidebarOpen={false}>
            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>
                {/* Page Header */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 20,
                    background: "#fff",
                    borderRadius: 10,
                    padding: "16px 20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                            Product Costing
                        </h2>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
                            Manage and track product costing
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openRunModal}
                        style={{
                            background: "#1e293b",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            padding: "8px 18px",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        Costing Run
                    </button>

                    {showRunModal && (
                        <div
                            onClick={() => { if (!costingRunning) setShowRunModal(false); }}
                            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16 }}
                        >
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ background: "#fff", borderRadius: 14, width: 440, maxWidth: "100%", boxShadow: "0 24px 60px rgba(15,23,42,0.35)", overflow: "hidden" }}
                            >
                                {/* Header */}
                                <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>Costing Run</h3>
                                        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                                            Update product master prices from actual unit cost
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        aria-label="Close"
                                        onClick={() => setShowRunModal(false)}
                                        disabled={costingRunning}
                                        style={{ background: "transparent", border: "none", fontSize: 22, lineHeight: 1, color: "#94a3b8", cursor: "pointer", padding: 0 }}
                                    >
                                        &times;
                                    </button>
                                </div>

                                {/* Body */}
                                <div style={{ padding: "20px 24px" }}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                                        Costing run type <span style={{ color: "#dc2626" }}>*</span>
                                    </label>
                                    <select
                                        value={runType}
                                        onChange={(e) => { setRunType(e.target.value); setRunPeriod(""); setRunMessage(""); setRunStatus("info"); }}
                                        disabled={costingRunning}
                                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, color: "#0f172a", background: "#fff", outline: "none" }}
                                    >
                                        <option value="">Select costing run type</option>
                                        <option value="base_metal">Base metal based</option>
                                        <option value="period">Period based</option>
                                    </select>

                                    {runType === "period" && (
                                        <div style={{ marginTop: 16 }}>
                                            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                                                Period <span style={{ color: "#dc2626" }}>*</span>
                                            </label>
                                            <select
                                                value={runPeriod}
                                                onChange={(e) => { setRunPeriod(e.target.value); setRunMessage(""); setRunStatus("info"); }}
                                                disabled={costingRunning}
                                                style={{ width: "100%", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, color: "#0f172a", background: "#fff", outline: "none" }}
                                            >
                                                <option value="">Select period</option>
                                                <option value="monthly">Monthly</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="previous_day">Previous day</option>
                                            </select>
                                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                                                {runPeriod === "monthly" && "Average actual unit cost of the current month."}
                                                {runPeriod === "weekly" && "Average actual unit cost of the last 7 days."}
                                                {runPeriod === "previous_day" && "Actual unit cost of the previous day."}
                                                {!runPeriod && "Prices in product master are updated for the selected period."}
                                            </div>
                                        </div>
                                    )}

                                    {runMessage && (
                                        <div style={{
                                            marginTop: 16,
                                            padding: "10px 12px",
                                            borderRadius: 8,
                                            fontSize: 13,
                                            border: "1px solid",
                                            background: runStatus === "success" ? "#f0fdf4" : runStatus === "error" ? "#fef2f2" : "#f8fafc",
                                            borderColor: runStatus === "success" ? "#bbf7d0" : runStatus === "error" ? "#fecaca" : "#e2e8f0",
                                            color: runStatus === "success" ? "#166534" : runStatus === "error" ? "#991b1b" : "#334155",
                                        }}>
                                            {runMessage}
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div style={{ padding: "14px 24px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowRunModal(false)}
                                        disabled={costingRunning}
                                        style={{ background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCostingRun}
                                        disabled={costingRunning}
                                        style={{ background: costingRunning ? "#94a3b8" : "#1e293b", color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: costingRunning ? "not-allowed" : "pointer" }}
                                    >
                                        {costingRunning ? "Running..." : "Run"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* FILTERS */}
                <div style={{
                    background: "#fff",
                    borderRadius: 10,
                    padding: "14px 20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    marginBottom: 20,
                }}>
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Type</label>
                            <Select
                                options={poFilterMasterData.productTypes}
                                value={selectedType ? { value: selectedType, label: selectedType } : null}
                                onChange={handleProductTypeChange}
                                isClearable
                                placeholder="Select Product Type"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>

                        <div className="col-md-3">
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Costing Level</label>
                            <Select
                                options={poFilterMasterData.costingLevels}
                                value={selectedCostingLevel ? { value: selectedCostingLevel, label: selectedCostingLevel } : null}
                                onChange={(opt) => setSelectedCostingLevel(opt ? opt.value : "")}
                                isClearable
                                placeholder="Select Costing Level"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>

                    {/* Row 1: type-specific filters (same sets as Product Master)
                        Raw Material          -> Category | Sub Category | Quality Category
                        Finished / Semi / none -> Category | Cable Type | Code | Color | Cores | Cross Section | Std. Ref | Voltage | Construction */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                        {(isRawMaterial
                            ? [
                                { key: "category", opts: filterOptions.category, label: "Category" },
                                { key: "subCategory", opts: filterOptions.subCategory, label: "Sub Category" },
                                { key: "qualityCategory", opts: filterOptions.qualityCategory, label: "Quality Category" },
                            ]
                            : [
                                { key: "category", opts: filterOptions.category, label: "Category" },
                                { key: "cableType", opts: filterOptions.cableType, label: "Cable Type" },
                                { key: "code", opts: filterOptions.code, label: "Code" },
                                { key: "color", opts: filterOptions.color, label: "Color" },
                                { key: "noOfCores", opts: filterOptions.noOfCores, label: "Cores" },
                                { key: "nominalCrossSection", opts: filterOptions.nominalCrossSection, label: "Cross Section" },
                                { key: "referenceOfStandard", opts: filterOptions.referenceOfStandard, label: "Std. Ref" },
                                { key: "voltage", opts: filterOptions.voltage, label: "Voltage" },
                                { key: "construction", opts: filterOptions.construction, label: "Construction" },
                            ]
                        ).map(({ key, opts, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                                <Select
                                    placeholder={label}
                                    isClearable
                                    options={toStrOptions(opts)}
                                    value={listingFilters[key]}
                                    onChange={opt => handleListingFilter(key, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={compactSelectStyles}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Row 2: Document | Product Code | Workcenter */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Document Type</label>
                            <Select
                                options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
                                value={selectedDocType ? { value: selectedDocType, label: selectedDocType } : null}
                                onChange={(opt) => {
                                    setSelectedDocType(opt ? opt.value : "");
                                    setSelectedProductionNo(""); // selected document may not belong to the new type
                                }}
                                isClearable placeholder="All Document Types"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Document</label>
                            <Select
                                options={documentOptions}
                                value={selectedProductionNo ? { value: selectedProductionNo, label: selectedProductionNo } : null}
                                onChange={(opt) => setSelectedProductionNo(opt ? opt.value : "")}
                                isClearable placeholder="Document"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Code</label>
                            <Select
                                options={productCodeMasterOptions}
                                value={listingProductCode}
                                onChange={(opt) => setListingProductCode(opt)}
                                isClearable placeholder="Product Code"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>

                        {!isSummary && (
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Workcenter</label>
                            <Select
                                options={workcenterNames.map((w) => ({ value: w, label: w }))}
                                value={selectedWorkcenter ? { value: selectedWorkcenter, label: selectedWorkcenter } : null}
                                onChange={(opt) => setSelectedWorkcenter(opt ? opt.value : "")}
                                isClearable placeholder="Workcenter"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={compactSelectStyles}
                            />
                        </div>
                        )}

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Creation Date From</label>
                            <DateField value={dateFrom} onChange={setDateFrom} placeholder="dd-mm-yyyy" max={dateTo || undefined} />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Creation Date To</label>
                            <DateField value={dateTo} onChange={setDateTo} placeholder="dd-mm-yyyy" min={dateFrom || undefined} />
                        </div>
                    </div>
                </div>

                {/* Detailed / Summary toggle */}
                <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 15 }}>
                    <div style={styles.tabBar}>
                        <button
                            type="button"
                            style={viewMode === "detailed" ? styles.tabBtnActive : styles.tabBtn}
                            onClick={() => setViewMode("detailed")}
                        >
                            Detailed
                        </button>
                        <button
                            type="button"
                            style={viewMode === "summary" ? styles.tabBtnActive : styles.tabBtn}
                            onClick={() => setViewMode("summary")}
                        >
                            Summary
                        </button>
                    </div>
                </div>

                {/* TABLE CONTAINER */}
                <div style={styles.tableContainer}>
                    <div style={{ overflowX: "auto" }}>
                        <table style={styles.erpTable}>
                            {isSummary ? (
                                <>
                                    <thead>
                                        <tr>
                                            <th rowSpan={2} style={styles.erpTh}>Product Type</th>
                                            <th rowSpan={2} style={styles.erpTh}>Product Code</th>
                                            <th rowSpan={2} style={styles.erpTh}>Description</th>
                                            <th rowSpan={2} style={{ ...styles.erpTh, textAlign: "right" }}>Qty</th>
                                            <th rowSpan={2} style={styles.erpTh}>UOM</th>
                                            <th rowSpan={2} style={styles.erpTh}>Currency</th>
                                            <th colSpan={3} style={{ ...styles.erpTh, textAlign: "center", borderLeft: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>Planned</th>
                                            <th colSpan={3} style={{ ...styles.erpTh, textAlign: "center", borderLeft: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>Actual</th>
                                        </tr>
                                        <tr>
                                            <th style={{ ...styles.erpTh, textAlign: "right", borderLeft: "1px solid #e5e7eb", minWidth: 130 }}>Unit Cost</th>
                                            <th style={{ ...styles.erpTh, textAlign: "right", minWidth: 150 }}>Material Cost</th>
                                            <th style={{ ...styles.erpTh, textAlign: "right", minWidth: 150 }}>Total Cost</th>
                                            <th style={{ ...styles.erpTh, textAlign: "right", borderLeft: "1px solid #e5e7eb" }}>Unit Cost</th>
                                            <th style={{ ...styles.erpTh, textAlign: "right", minWidth: 150 }}>Material Cost</th>
                                            <th style={{ ...styles.erpTh, textAlign: "right", minWidth: 150 }}>Total Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.length === 0 ? (
                                            <tr>
                                                <td colSpan={12} style={{ ...styles.erpTd, textAlign: "center", padding: "24px 12px", color: "#94a3b8" }}>
                                                    No records found
                                                </td>
                                            </tr>
                                        ) : (
                                            currentOrders.map((r) => (
                                                <tr key={r.product_code}>
                                                    <td style={styles.erpTd}>{r.productType}</td>
                                                    <td style={styles.erpTd}>
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(r.product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        >
                                                            {r.product_code}
                                                        </Link>
                                                    </td>
                                                    <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.description}>{r.description}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>{r.qty}</td>
                                                    <td style={styles.erpTd}>{r.uom}</td>
                                                    <td style={styles.erpTd}>{r.currency}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.planned_unit_cost ?? ""}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.planned_material_cost ?? ""}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.planned_cost ?? ""}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.unit_cost ?? ""}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.actual_material_cost ?? ""}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>{r.actual_cost ?? ""}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </>
                            ) : (
                                <>
                            <thead>
                                <tr>
                                    <th rowSpan={2} style={styles.erpTh}>Document</th>
                                    <th rowSpan={2} style={{ ...styles.erpTh, minWidth: 140 }}>Transaction Document</th>
                                    <th rowSpan={2} style={{ ...styles.erpTh, minWidth: 110 }}>Creation Date</th>
                                    <th rowSpan={2} style={styles.erpTh}>Batch Number</th>
                                    <th rowSpan={2} style={styles.erpTh}>Product Type</th>
                                    <th rowSpan={2} style={styles.erpTh}>Product Code</th>
                                    <th rowSpan={2} style={styles.erpTh}>Description</th>
                                    <th rowSpan={2} style={{ ...styles.erpTh, textAlign: "right" }}>Qty</th>
                                    <th rowSpan={2} style={styles.erpTh}>UOM</th>
                                    <th rowSpan={2} style={styles.erpTh}>Workcenter</th>
                                    <th rowSpan={2} style={styles.erpTh}>Currency</th>
                                    <th colSpan={3} style={{ ...styles.erpTh, textAlign: "center", borderLeft: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>Planned</th>
                                    <th colSpan={3} style={{ ...styles.erpTh, textAlign: "center", borderLeft: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>Actual</th>
                                </tr>
                                <tr>
                                    <th style={{ ...styles.erpTh, textAlign: "right", borderLeft: "1px solid #e5e7eb", width: 130, minWidth: 130 }}>Unit Cost</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right", width: 150, minWidth: 150 }}>Material Cost</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right", width: 150, minWidth: 150 }}>Total Cost</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right", borderLeft: "1px solid #e5e7eb" }}>Unit Cost</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right", width: 150, minWidth: 150 }}>Material Cost</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right", width: 150, minWidth: 150 }}>Total Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={17} style={{ ...styles.erpTd, textAlign: "center", padding: "24px 12px", color: "#94a3b8" }}>
                                            No records found
                                        </td>
                                    </tr>
                                ) : (
                                    currentOrders.map((o, idx) => {
                                        // Physical Inventory rows (opening stock, PINV-xxxx) have no PO / GRN / production
                                        // order, so they render like flat goods-receipt rows: plain document text, no links, no breakdown.
                                        const isPhysicalInventory = o.documentType === "Physical Inventory";
                                        const isGoodsReceipt = o.sourceType === "goods_receipt" || isPhysicalInventory;
                                        const isPurchaseOrder = o.documentType === "Purchase Order";
                                        const documentValue = isGoodsReceipt
                                            ? o.document
                                            : (isPurchaseOrder ? o.purchase_order_number : o.production_order_number);
                                        const rowKey = `${o.sourceType || o.documentType || "production"}-${documentValue || "doc"}-${o.batch_no || idx}`;
                                        const materialOpen = !isGoodsReceipt && !isPurchaseOrder && !!expandedMaterialRows[rowKey];
                                        const plannedMaterialOpen = !isGoodsReceipt && !isPurchaseOrder && !!expandedPlannedMaterialRows[rowKey];
                                        return (
                                        <React.Fragment key={rowKey}>
                                        <tr>
                                            <td style={{ ...styles.erpTd, fontWeight: 600, color: "#1e293b" }}>
                                                {isPhysicalInventory ? (
                                                    // Physical inventory (PINV-xxxx) has no screen to open
                                                    documentValue || ""
                                                ) : (isGoodsReceipt && documentValue) ? (
                                                    <Link
                                                        to={`/cable/procurement/purchase-order?view=${encodeURIComponent(documentValue)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {documentValue}
                                                    </Link>
                                                ) : isGoodsReceipt ? (
                                                    ""
                                                ) : isPurchaseOrder ? (
                                                    <Link
                                                        to={`/cable/procurement/purchase-order?view=${encodeURIComponent(o.purchase_order_number)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.purchase_order_number}
                                                    </Link>
                                                ) : (
                                                    <Link
                                                        to={`/cable/production-order?view=${encodeURIComponent(o.production_order_number)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.production_order_number}
                                                    </Link>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {o.transaction_document && (isGoodsReceipt || isPurchaseOrder) ? (
                                                    <Link
                                                        to={`/cable/goods-receipt/goods-receipt?view=${encodeURIComponent(o.transaction_document)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.transaction_document}
                                                    </Link>
                                                ) : o.transaction_document ? (
                                                    // Production order rows: transaction_document is the confirmation id
                                                    <Link
                                                        to={`/cable/confirmation?view=${encodeURIComponent(o.transaction_document)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.transaction_document}
                                                    </Link>
                                                ) : (
                                                    ""
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, whiteSpace: "nowrap" }}>{formatDMY(o.transaction_created_at)}</td>
                                            <td style={styles.erpTd}>{o.batch_no || ""}</td>
                                            <td style={styles.erpTd}>{o.productType || o.product_type || ""}</td>
                                            <td style={styles.erpTd}>
                                                {(o.header_product_code || o.product_code) ? (
                                                    <Link
                                                        to={`/cable/product/product-code?view=${encodeURIComponent(o.header_product_code || o.product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.header_product_code || o.product_code}
                                                    </Link>
                                                ) : (
                                                    ""
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.productDescription || o.description || ""}>
                                                {o.productDescription || o.description || ""}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>{o.general_qty || o.quantity || o.order_qty || ""}</td>
                                            <td style={styles.erpTd}>{o.uom || ""}</td>
                                            <td style={styles.erpTd}>
                                                {/* Goods Receipt rows have no workcenter -- goods_receipt table doesn't carry one */}
                                                {!isGoodsReceipt && (o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description) ? (
                                                    <Link
                                                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                        style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description}
                                                    </Link>
                                                ) : (
                                                    isGoodsReceipt ? <span style={{ color: "#cbd5e1" }}>—</span> : ""
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>{o.currency || ""}</td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                {isGoodsReceipt ? <span style={{ color: "#cbd5e1" }}>—</span> : (o.planned_unit_cost != null ? `${o.planned_unit_cost}` : "")}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                {isGoodsReceipt ? (
                                                    <span style={{ color: "#cbd5e1" }}>—</span>
                                                ) : isPurchaseOrder ? (
                                                    // Purchase Order rows have a single flat rate (movingAveragePrice),
                                                    // not a BOM breakdown -- no line-item drill-down to show.
                                                    <span>{o.planned_material_cost != null ? `${o.planned_material_cost}` : ""}</span>
                                                ) : (
                                                    <span
                                                        onClick={() => setExpandedPlannedMaterialRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 6, width: "100%", cursor: "pointer", userSelect: "none" }}
                                                        title="Show planned material breakdown"
                                                    >
                                                        <span style={{
                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                            width: 14, height: 14, borderRadius: 4, background: "#e2e8f0",
                                                            color: "#475569", fontSize: 9, transform: plannedMaterialOpen ? "rotate(90deg)" : "rotate(0deg)",
                                                            transition: "transform 0.15s"
                                                        }}>▶</span>
                                                        {o.planned_material_cost ?? "0.00"}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                {isGoodsReceipt ? (
                                                    <span>{o.planned_cost ?? "0.00"}</span>
                                                ) : isPurchaseOrder ? (
                                                    <span>{o.planned_cost != null ? `${o.planned_cost}` : ""}</span>
                                                ) : (
                                                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
                                                        {o.planned_cost ?? "0.00"}
                                                        <CostBreakdownPopover
                                                            label="Planned Cost"
                                                            color="#1e293b"
                                                            productionOrderNumber={o.production_order_number}
                                                            mode="planned"
                                                            data={{
                                                                material: o.planned_material_cost,
                                                                setup: o.planned_setup_cost,
                                                                labour: o.planned_labour_cost,
                                                                machine: o.planned_machine_cost,
                                                                overhead: o.planned_overhead_cost,
                                                            }}
                                                        />
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                {o.unit_cost != null ? `${o.unit_cost}` : ""}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                {isGoodsReceipt ? (
                                                    <span>{o.actual_cost != null ? `${o.actual_cost}` : ""}</span>
                                                ) : isPurchaseOrder ? (
                                                    // Purchase Order rows have a single flat material cost
                                                    // (from the GRN), not a BOM breakdown -- no line-item
                                                    // drill-down to show, same as the Planned column.
                                                    <span>{o.actual_cost != null ? `${o.actual_cost}` : ""}</span>
                                                ) : (
                                                    <span
                                                        onClick={() => setExpandedMaterialRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 6, width: "100%", cursor: "pointer", userSelect: "none" }}
                                                        title="Show material breakdown"
                                                    >
                                                        <span style={{
                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                            width: 14, height: 14, borderRadius: 4, background: "#e2e8f0",
                                                            color: "#475569", fontSize: 9, transform: materialOpen ? "rotate(90deg)" : "rotate(0deg)",
                                                            transition: "transform 0.15s"
                                                        }}>▶</span>
                                                        {o.actual_cost ?? "0.00"}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
                                                    {isPurchaseOrder ? (o.actual_cost != null ? `${o.actual_cost}` : "") : `${o.actual_cost ?? "0.00"}`}
                                                    {/* Goods Receipt cost is a single flat figure (final_price_after_gst) --
                                                        no material/setup/labour/machine/overhead breakdown exists for it,
                                                        and Purchase Orders never have an actual_product_costing row --
                                                        so skip the popover for both. */}
                                                    {!isGoodsReceipt && !isPurchaseOrder && (
                                                        <CostBreakdownPopover
                                                            label="Actual Cost"
                                                            color="#0369a1"
                                                            productionOrderNumber={o.production_order_number}
                                                            mode="actual"
                                                            data={{
                                                                material: o.actual_material_cost,
                                                                setup: o.actual_setup_cost,
                                                                labour: o.actual_labour_cost,
                                                                machine: o.actual_machine_cost,
                                                                overhead: o.actual_overhead_cost,
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            </td>
                                            {/* <td style={styles.erpTd}>{o.release_status || o.status || "Pending"}</td> */}
                                        </tr>
                                        {plannedMaterialOpen && (
                                            <tr>
                                                <td colSpan={17} style={{ padding: 0, background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                                                    <div style={{ maxWidth: 560, marginLeft: "auto", marginRight: 12 }}>
                                                        <MaterialLineItems productionOrderNumber={o.production_order_number} mode="planned" />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {materialOpen && (
                                            <tr>
                                                <td colSpan={17} style={{ padding: 0, background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                                                    <div style={{ maxWidth: 700, marginLeft: "auto", marginRight: 12 }}>
                                                        <MaterialLineItems productionOrderNumber={o.production_order_number} mode="actual" />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                                </>
                            )}
                        </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#f8fafc", borderTop: "1px solid #e5e7eb" }}>
                        <div style={{ fontSize: 13, color: "#64748b" }}>
                            Showing {filteredOrders.length === 0 ? 0 : indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} entries
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Rows per page:</span>
                                <select 
                                    value={itemsPerPage} 
                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    style={{ fontSize: 12, padding: "4px 6px", border: "1px solid #cbd5e1", borderRadius: 4, outline: "none", cursor: "pointer", color: "#334155", background: "#fff" }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <button 
                                    onClick={handlePrevPage} 
                                    disabled={currentPage === 1}
                                    style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, background: currentPage === 1 ? "#f1f5f9" : "#fff", color: currentPage === 1 ? "#94a3b8" : "#334155", border: "1px solid #cbd5e1", borderRadius: 4, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                                >
                                    Prev
                                </button>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", padding: "4px 8px" }}>
                                    {currentPage} / {totalPages || 1}
                                </span>
                                <button 
                                    onClick={handleNextPage} 
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, background: currentPage === totalPages || totalPages === 0 ? "#f1f5f9" : "#fff", color: currentPage === totalPages || totalPages === 0 ? "#94a3b8" : "#334155", border: "1px solid #cbd5e1", borderRadius: 4, cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const styles = {
    tabBar: { display: "inline-flex", gap: "2px", background: "#f1f5f9", borderRadius: "10px", padding: "4px" },
    tabBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", fontSize: "13px", fontWeight: "600", borderRadius: "8px", border: "none", background: "transparent", color: "#64748b", cursor: "pointer" },
    tabBtnActive: { display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", fontSize: "13px", fontWeight: "600", borderRadius: "8px", border: "none", background: "#fff", color: "#0f172a", boxShadow: "0 1px 3px rgba(0,0,0,0.12)", cursor: "pointer" },
    tableContainer: {
        background: "#ffffff",
        borderRadius: 8,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        overflow: "hidden", // Ensures the pagination bar corners clip correctly to the container
    },
    erpTable: {
        width: "max-content",
        minWidth: "100%",
        tableLayout: "auto",
        borderCollapse: "collapse",
    },
    erpTh: {
        background: "#f8fafc",
        fontSize: 13,
        fontWeight: 600,
        padding: "10px 12px",
        borderBottom: "1px solid #e5e7eb",
        color: "#1e293b",
        whiteSpace: "nowrap",
    },
    erpTd: {
        fontSize: 13,
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155",
        verticalAlign: "middle",
    },
};

export default ProductCosting;